import React, { useState, useEffect } from "react";
import { Check, Plus, Trash2, Edit2, X, ChevronDown } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { cn } from "@shadcn-in-nx/lib/utils";

const TaskList = ({
    tasks,
    onUpdateTask,
    onAddTask,
    onDeleteTask,
    disabled = false,
}) => {
    const [newTaskContent, setNewTaskContent] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editingContent, setEditingContent] = useState("");
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return tasks.length > 0 && tasks.every((t) => t.status === "completed");
    });

    useEffect(() => {
        if (tasks.length > 0 && tasks.every((t) => t.status === "completed")) {
            setIsCollapsed(true);
        }
    }, [tasks]);

    const completed = tasks.filter((t) => t.status === "completed").length;
    const progress = tasks.length > 0 ? (completed / tasks.length) * 100 : 0;

    const handleAddTask = () => {
        if (newTaskContent.trim()) {
            onAddTask(newTaskContent.trim());
            setNewTaskContent("");
            setIsAdding(false);
        }
    };

    const handleStartEdit = (task) => {
        if (disabled) return;
        setEditingTaskId(task.id);
        setEditingContent(task.content);
    };

    const handleSaveEdit = () => {
        if (editingTaskId && editingContent.trim()) {
            onUpdateTask(editingTaskId, { content: editingContent.trim() });
            setEditingTaskId(null);
            setEditingContent("");
        }
    };

    const handleCancelEdit = () => {
        setEditingTaskId(null);
        setEditingContent("");
    };

    const handleToggleStatus = (task) => {
        if (disabled) return;
        const newStatus = task.status === "completed" ? "pending" : "completed";
        onUpdateTask(task.id, { status: newStatus });
    };

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700/60 mb-3 overflow-hidden bg-white dark:bg-gray-900/40">
            {/* Header */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Tasks
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {completed}/{tasks.length}
                    </span>
                    {/* Progress bar */}
                    <div className="flex-1 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden max-w-[80px]">
                        <div
                            className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
                <ChevronDown className={cn(
                    "h-3.5 w-3.5 text-gray-400 transition-transform duration-200",
                    !isCollapsed && "rotate-180"
                )} />
            </button>

            {/* Tasks */}
            {!isCollapsed && (
                <div className="px-3 pb-2.5">
                    <div className="space-y-px">
                        {tasks.map((task) => (
                            <div
                                key={task.id}
                                className={cn(
                                    "flex items-start gap-2 py-1.5 px-1.5 rounded-lg transition-colors group",
                                    disabled && "opacity-60 pointer-events-none"
                                )}
                            >
                                {/* Custom checkbox */}
                                <button
                                    onClick={() => handleToggleStatus(task)}
                                    disabled={disabled}
                                    className={cn(
                                        "mt-0.5 w-4 h-4 rounded-[4px] border-[1.5px] flex items-center justify-center shrink-0 transition-all duration-150",
                                        task.status === "completed"
                                            ? "bg-blue-500 border-blue-500"
                                            : "border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                    )}
                                >
                                    {task.status === "completed" && (
                                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                    )}
                                </button>

                                <div className="flex-1 min-w-0">
                                    {editingTaskId === task.id ? (
                                        <div className="flex items-center gap-1.5">
                                            <Input
                                                value={editingContent}
                                                onChange={(e) => setEditingContent(e.target.value)}
                                                className="h-7 text-xs"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleSaveEdit();
                                                    if (e.key === "Escape") handleCancelEdit();
                                                }}
                                                autoFocus
                                                disabled={disabled}
                                            />
                                            <button onClick={handleSaveEdit} disabled={disabled} className="text-green-600 hover:text-green-700 p-1">
                                                <Check className="h-3 w-3" />
                                            </button>
                                            <button onClick={handleCancelEdit} disabled={disabled} className="text-gray-400 hover:text-gray-600 p-1">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className={cn(
                                            "text-xs leading-relaxed break-words",
                                            task.status === "completed"
                                                ? "text-gray-400 dark:text-gray-500 line-through"
                                                : "text-gray-700 dark:text-gray-300"
                                        )}>
                                            {task.content}
                                        </span>
                                    )}
                                </div>

                                {editingTaskId !== task.id && !disabled && (
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                            onClick={() => handleStartEdit(task)}
                                        >
                                            <Edit2 className="h-3 w-3" />
                                        </button>
                                        <button
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            onClick={() => onDeleteTask(task.id)}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Add task */}
                    {isAdding ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                            <Input
                                value={newTaskContent}
                                onChange={(e) => setNewTaskContent(e.target.value)}
                                placeholder="New task..."
                                className="h-7 text-xs"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAddTask();
                                    if (e.key === "Escape") setIsAdding(false);
                                }}
                                autoFocus
                                disabled={disabled}
                            />
                            <Button
                                size="sm"
                                onClick={handleAddTask}
                                disabled={!newTaskContent.trim() || disabled}
                                className="h-7 text-xs px-2.5"
                            >
                                Add
                            </Button>
                            <button
                                onClick={() => setIsAdding(false)}
                                disabled={disabled}
                                className="text-gray-400 hover:text-gray-600 p-1"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ) : (
                        <button
                            className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-blue-600 transition-colors pl-1.5"
                            onClick={() => setIsAdding(true)}
                            disabled={disabled}
                        >
                            <Plus className="h-3 w-3" />
                            Add Task
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default TaskList;
